/**
 * LabsCard — a contact's lab/test results (crm.contact_lab_results). Same card
 * grammar as ProfileListCard (add / edit / verify / two-tap delete, AI chips +
 * source excerpt), but labs carry a value + unit + reference range + draw date
 * rather than a status. The nightly AI profile builder writes these as
 * source='ai_extraction' (unconfirmed until a recruiter Verifies); recruiters
 * can also add/edit them by hand.
 */
import { FormEvent, useCallback, useEffect, useState } from 'react';
import { Check, FlaskConical, Loader2, Pencil, Plus, Trash2, X } from 'lucide-react';
import api from '../../../services/api';
import { LabResult } from '../../types';
import { relTime, useTeam } from '../../lib';
import { SourceChips } from './ProfileListCard';

const errMsg = (e: any): string =>
  e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Request failed';

/** "156 mg/dL" / "positive" / "7.2 %". */
function labValue(l: LabResult): string {
  const v = l.value_num != null ? String(l.value_num) : (l.value_text || '');
  return [v, l.unit].filter(Boolean).join(' ').trim();
}

/** "ref 70–99" when a range is on file, else ''. */
function labRef(l: LabResult): string {
  if (l.reference_low == null && l.reference_high == null) return '';
  return `ref ${l.reference_low ?? '…'}–${l.reference_high ?? '…'}`;
}

const FLAG_STYLES: Record<string, string> = {
  high: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  low: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  abnormal: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  normal: 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300',
};

/** Send a free-text value as value_num when it parses to a number, else value_text. */
function valueToBody(value: string): Record<string, unknown> {
  const t = value.trim();
  if (!t) return { value_num: null, value_text: null };
  const n = Number(t);
  return Number.isFinite(n) ? { value_num: n, value_text: null } : { value_num: null, value_text: t };
}

interface Props {
  contactId: string;
  reloadToken: number;
}

export default function LabsCard({ contactId, reloadToken }: Props) {
  const { byId } = useTeam();
  const [items, setItems] = useState<LabResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());
  const [armedDelete, setArmedDelete] = useState<string | null>(null);

  // Add form
  const [formOpen, setFormOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [value, setValue] = useState('');
  const [unit, setUnit] = useState('');
  const [resultDate, setResultDate] = useState('');
  const [dedupeMsg, setDedupeMsg] = useState<string | null>(null);

  // Edit
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editValue, setEditValue] = useState('');
  const [editUnit, setEditUnit] = useState('');
  const [editDate, setEditDate] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/api/crm/contacts/${contactId}/labs`);
      setItems(res.data?.items || []);
      setError(null);
    } catch (e: any) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [contactId]);

  useEffect(() => { load(); }, [load, reloadToken]);

  const setBusy = (id: string, busy: boolean) =>
    setBusyIds((prev) => {
      const n = new Set(prev);
      if (busy) n.add(id); else n.delete(id);
      return n;
    });

  const create = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim() || saving) return;
    setSaving(true);
    setActionError(null);
    setDedupeMsg(null);
    try {
      const body: Record<string, unknown> = { test_name: name.trim(), ...valueToBody(value) };
      if (unit.trim()) body.unit = unit.trim();
      if (resultDate) body.result_date = resultDate;
      const res = await api.post(`/api/crm/contacts/${contactId}/labs`, body);
      const item: LabResult | undefined = res.data?.item;
      if (item) {
        if (res.data?.deduped) {
          setDedupeMsg(`"${item.test_name}"${item.result_date ? ` on ${item.result_date}` : ''} is already on file.`);
        } else {
          setItems((prev) => [item, ...prev]);
          setName(''); setValue(''); setUnit(''); setResultDate('');
        }
      }
    } catch (e: any) {
      setActionError(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const verify = async (item: LabResult) => {
    if (busyIds.has(item.id)) return;
    setActionError(null);
    setBusy(item.id, true);
    try {
      const res = await api.post(`/api/crm/labs/${item.id}/verify`);
      const updated: LabResult | undefined = res.data?.item;
      if (updated) setItems((p) => p.map((i) => (i.id === item.id ? updated : i)));
    } catch (e: any) {
      setActionError(errMsg(e));
    } finally {
      setBusy(item.id, false);
    }
  };

  /** Two-tap delete: first tap arms (3s), second deletes. */
  const remove = async (item: LabResult) => {
    if (armedDelete !== item.id) {
      setArmedDelete(item.id);
      window.setTimeout(() => setArmedDelete((a) => (a === item.id ? null : a)), 3000);
      return;
    }
    setArmedDelete(null);
    setActionError(null);
    setBusy(item.id, true);
    setItems((p) => p.filter((i) => i.id !== item.id));
    try {
      await api.delete(`/api/crm/labs/${item.id}`);
    } catch (e: any) {
      setItems((p) => [...p, item].sort((a, b) => b.created_at.localeCompare(a.created_at)));
      setActionError(errMsg(e));
    } finally {
      setBusy(item.id, false);
    }
  };

  const startEdit = (item: LabResult) => {
    setEditingId(item.id);
    setEditName(item.test_name);
    setEditValue(item.value_num != null ? String(item.value_num) : (item.value_text || ''));
    setEditUnit(item.unit || '');
    setEditDate(item.result_date || '');
  };

  const saveEdit = async (item: LabResult) => {
    if (!editName.trim() || busyIds.has(item.id)) return;
    setActionError(null);
    setBusy(item.id, true);
    const patch: Record<string, unknown> = {
      test_name: editName.trim(),
      ...valueToBody(editValue),
      unit: editUnit.trim() || null,
      result_date: editDate || null,
    };
    setItems((p) => p.map((i) => (i.id === item.id ? { ...i, ...patch } as LabResult : i)));
    try {
      const res = await api.patch(`/api/crm/labs/${item.id}`, patch);
      const updated: LabResult | undefined = res.data?.item;
      if (updated) setItems((p) => p.map((i) => (i.id === item.id ? updated : i)));
      setEditingId(null);
    } catch (e: any) {
      setItems((p) => p.map((i) => (i.id === item.id ? item : i)));
      setActionError(errMsg(e));
    } finally {
      setBusy(item.id, false);
    }
  };

  const inputCls =
    'w-full rounded-lg border border-gray-300 bg-white px-2.5 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 min-h-[44px] sm:min-h-0';

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="inline-flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100">
          <FlaskConical className="h-4 w-4 text-primary-600 dark:text-primary-400" />
          Lab results
          {items.length > 0 && (
            <span className="text-sm font-normal text-gray-500 dark:text-gray-400">({items.length})</span>
          )}
        </h3>
        <button
          type="button"
          onClick={() => { setFormOpen((o) => !o); setDedupeMsg(null); }}
          aria-label={formOpen ? 'Close the add form' : 'Add a lab result'}
          className="inline-flex min-h-[44px] items-center gap-1 rounded-lg px-2 py-1 text-sm font-medium text-primary-700 transition-colors duration-150 hover:bg-primary-50 dark:text-primary-400 dark:hover:bg-primary-900/20 sm:min-h-0"
        >
          {formOpen ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
          {formOpen ? 'Close' : 'Add'}
        </button>
      </div>

      {formOpen && (
        <form onSubmit={create} className="mb-3 space-y-2 rounded-lg bg-gray-50 p-3 dark:bg-gray-900/40">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Test name (e.g. A1c, fasting glucose)"
            maxLength={200}
            className={inputCls}
          />
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            <input value={value} onChange={(e) => setValue(e.target.value)} placeholder="Value" maxLength={200} className={inputCls} />
            <input value={unit} onChange={(e) => setUnit(e.target.value)} placeholder="Unit" maxLength={40} className={inputCls} />
            <input value={resultDate} onChange={(e) => setResultDate(e.target.value)} type="date" aria-label="Result date" className={inputCls} />
          </div>
          <div className="flex items-center justify-between gap-2">
            {dedupeMsg ? <p className="text-xs text-amber-600 dark:text-amber-400">{dedupeMsg}</p> : <span />}
            <button
              type="submit"
              disabled={!name.trim() || saving}
              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0"
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
              Add
            </button>
          </div>
        </form>
      )}

      {actionError && (
        <p role="alert" className="mb-2 text-xs text-red-600 dark:text-red-400">{actionError}</p>
      )}

      {loading ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
          {error}{' '}
          <button onClick={() => { setLoading(true); load(); }} className="font-medium underline">Retry</button>
        </div>
      ) : items.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          No lab results recorded — add what they mention, or let the nightly AI pull them from faxed records.
        </p>
      ) : (
        <ul className="divide-y divide-gray-100 dark:divide-gray-700">
          {items.map((item) => {
            const busy = busyIds.has(item.id);
            const verifier = item.verified_by ? byId.get(item.verified_by)?.name : null;
            const flag = item.abnormal_flag && item.abnormal_flag !== 'unknown' ? item.abnormal_flag : null;
            const ref = labRef(item);
            return (
              <li key={item.id} className="py-2.5">
                {editingId === item.id ? (
                  <div className="space-y-2">
                    <input value={editName} onChange={(e) => setEditName(e.target.value)} aria-label="test name" maxLength={200} className={inputCls} />
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                      <input value={editValue} onChange={(e) => setEditValue(e.target.value)} placeholder="Value" maxLength={200} className={inputCls} />
                      <input value={editUnit} onChange={(e) => setEditUnit(e.target.value)} placeholder="Unit" maxLength={40} className={inputCls} />
                      <input value={editDate} onChange={(e) => setEditDate(e.target.value)} type="date" aria-label="Result date" className={inputCls} />
                    </div>
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => setEditingId(null)}
                        className="min-h-[44px] rounded-lg border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 sm:min-h-0"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={() => saveEdit(item)}
                        disabled={!editName.trim() || busy}
                        className="inline-flex min-h-[44px] items-center gap-1 rounded-lg bg-primary-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-primary-700 disabled:opacity-50 sm:min-h-0"
                      >
                        {busy && <Loader2 className="h-3 w-3 animate-spin" />} Save
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{item.test_name}</span>
                        {labValue(item) && (
                          <span className="ml-1.5 text-sm font-semibold text-gray-700 dark:text-gray-200">{labValue(item)}</span>
                        )}
                        {flag && (
                          <span className={`ml-1.5 inline-flex items-center rounded-full px-1.5 py-0.5 text-[11px] font-semibold ${FLAG_STYLES[flag] || 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'}`}>
                            {flag}
                          </span>
                        )}
                        <span className="ml-1.5 text-xs text-gray-400">
                          {[item.specimen_context, ref, item.result_date].filter(Boolean).join(' · ')}
                        </span>
                      </div>
                      <div className="flex shrink-0 items-center gap-1">
                        <button
                          type="button"
                          onClick={() => startEdit(item)}
                          aria-label={`Edit ${item.test_name}`}
                          className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg p-1 text-gray-400 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-300 sm:min-h-0 sm:min-w-0"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => remove(item)}
                          disabled={busy}
                          aria-label={armedDelete === item.id ? `Confirm delete ${item.test_name}` : `Delete ${item.test_name}`}
                          title={armedDelete === item.id ? 'Click again to confirm' : 'Delete'}
                          className={`inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg p-1 transition-colors duration-150 sm:min-h-0 sm:min-w-0 ${
                            armedDelete === item.id
                              ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'
                              : 'text-gray-400 hover:bg-gray-100 hover:text-red-600 dark:hover:bg-gray-700'
                          }`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                    <div className="mt-1.5 flex flex-wrap items-center gap-2">
                      <SourceChips item={item} />
                      {item.verified_at ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 dark:text-green-400">
                          <Check className="h-3.5 w-3.5" />
                          Verified{verifier ? ` · ${verifier}` : ''} · {relTime(item.verified_at)}
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => verify(item)}
                          disabled={busy}
                          title="Mark this as confirmed with the patient"
                          className="inline-flex min-h-[44px] items-center rounded-lg border border-gray-300 px-2 py-0.5 text-xs font-medium text-gray-500 transition-colors duration-150 hover:border-green-400 hover:text-green-700 disabled:opacity-50 dark:border-gray-600 dark:text-gray-400 dark:hover:text-green-400 sm:min-h-0"
                        >
                          Verify
                        </button>
                      )}
                    </div>
                    {item.source === 'ai_extraction' && item.source_excerpt && (
                      <p className="mt-1 border-l-2 border-primary-200 pl-2 text-xs italic text-gray-500 dark:border-primary-800 dark:text-gray-400">
                        “{item.source_excerpt}”
                      </p>
                    )}
                  </>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
